import type { SnakeCase } from "type-fest";
import type { ZodType } from "zod";
import z from "zod";

/**
 * What a recorded action was.
 *
 * A const object with a literal-union type rather than a TypeScript `enum`:
 * enums emit runtime code and are not erasable, and the union is what lets the
 * data registry be checked as total. Written out as a type as well as a schema
 * because tsoa follows a type into the API contract but cannot follow
 * `z.infer`.
 *
 * Where a request records its own act, the kind is the request's event name
 * respelt in snake_case, so `person.archived` is `person_archived`. Keyed by
 * the value itself, so a search for a stored kind finds every writer.
 *
 * A value is stored in the database exactly as written here, so renaming one
 * rewrites history — existing rows keep the old string and stop matching the new
 * name. Add a member; never rename one.
 *
 * Removing one is the same hazard and then some, because the read is strict: a
 * stored row whose kind is no longer a member makes the whole page unparseable,
 * not just that entry. So a member may only go while nothing has written it.
 *
 * Reads are deliberately not recorded, `admin_person_read`,
 * `assessment_pdf_downloaded` and `shared_assessment_read` aside. An entry
 * per read is an entry per keystroke in a viewer that re-searches as it is
 * narrowed, and each one is returned by the next search — a trail that grows
 * by being looked at, and whose bulk is the looking rather than the doing. Who
 * read what is answered by the request log, which costs the trail nothing.
 */
export const AuditTrailEntryKind = {
  /**
   * An account was created: by an administrator, before its owner had signed
   * in, or by its owner signing in for the first time. The entry's author says
   * which, and an administrator is who vouched for the address.
   */
  user_created: "user_created",
  /**
   * Somebody asked the identity provider to email an account a link to set its
   * password. Recorded because it is how a password comes to be chosen, and the
   * email itself leaves no trace here.
   */
  user_password_reset_sent: "user_password_reset_sent",
  /** An existing account was attached to an identity-provider subject. */
  user_linked_to_identity_provider: "user_linked_to_identity_provider",
  session_sign_in_success: "session_sign_in_success",
  /**
   * A sign-in the identity provider allowed was refused here — a Thalamos
   * address outside the administrators directory group.
   */
  session_sign_in_failure: "session_sign_in_failure",
  session_signed_out: "session_signed_out",
  /** The session context a session acts in was chosen. */
  session_context_chosen: "session_context_chosen",
  /**
   * What an account says about the person behind it was changed — by them, or by
   * somebody administering them.
   *
   * One kind for both, because the entry already records who acted and in what
   * session context: a separate kind for an administrator's edit would say the
   * same thing twice and let the two drift.
   */
  user_profile_updated: "user_profile_updated",
  /**
   * The administrative membership was brought into line with the directory
   * group. Recorded either way: both change who can reach a customer's
   * accounts, and neither is visible from the account row afterwards.
   */
  administrative_membership_granted: "administrative_membership_granted",
  administrative_membership_revoked: "administrative_membership_revoked",
  /**
   * Somebody granted an account a membership at a customer organisation, or
   * took one away.
   *
   * Distinct from the administrative pair above, which record a reconciliation
   * against a directory group rather than a decision: these two have an author,
   * and the author is the point. A grant is somebody vouching that conditions
   * Haven cannot check — training, most of all — were met.
   *
   * Written only where the set of memberships actually changed. Asking for a
   * membership already held is answered, but it decided nothing, and an entry
   * saying otherwise would make the trail's answer to "who granted this" the
   * last person to open the page.
   */
  membership_granted: "membership_granted",
  membership_revoked: "membership_revoked",
  /** A young person's record was registered. */
  person_created: "person_created",
  /**
   * A case was set up on a young person's record, as one act naming everybody
   * involved in it from the start.
   *
   * Recorded beside the involvement entries that act writes, not instead of
   * them: each grant is still somebody being given reach, and this says they
   * were given it together.
   */
  person_enrolled: "person_enrolled",
  /**
   * What a young person's record says about them — their names and date of
   * birth — was replaced. Not their identifiers: those are stated when the
   * record is registered and a correction cannot change one.
   */
  person_updated: "person_updated",
  /**
   * Two records were declared to be one young person. Both records are named
   * as resources; the payload says which survived.
   */
  person_merged: "person_merged",
  /** A merge was undone, and the merged record stands on its own again. */
  person_unmerged: "person_unmerged",
  /**
   * A young person's profile was archived, so no assessment can be started
   * from it until it is unarchived.
   */
  person_archived: "person_archived",
  /** An archived profile was unarchived. */
  person_unarchived: "person_unarchived",
  /**
   * A Thalamos admin read a young person's record by its id, seeing how it is
   * handled with the child's identity withheld. Recorded, unlike other reads,
   * because staff reaching a child's record is an act the people answerable
   * for the child are owed sight of, and such a read is a deliberate step, not
   * a keystroke.
   */
  admin_person_read: "admin_person_read",
  /**
   * An assessment of a young person was started, pinning the instrument version
   * it will be validated against and what the record then said.
   */
  assessment_started: "assessment_started",
  /**
   * The owner began filling an assessment in on the form, starting a draft of
   * its next attempt. Saves are not recorded: a draft is saved far more often
   * than anyone acts on it, and its answer stamps already say who saved each
   * answer.
   */
  assessment_draft_started: "assessment_draft_started",
  /**
   * A document was submitted against an assessment's instrument and accepted.
   *
   * One entry per attempt, so each is an act by whoever performed it.
   */
  assessment_submitted: "assessment_submitted",
  /**
   * An assessment was taken out of use. What was submitted against it is
   * untouched; the entry records the act, and why it was deleted stays on the
   * record where reach governs who reads it.
   */
  assessment_deleted: "assessment_deleted",
  /** A deleted assessment was put back, and stands again. */
  assessment_undeleted: "assessment_undeleted",
  /**
   * Somebody downloaded an assessment as a PDF. Recorded, unlike other reads,
   * because a PDF is a copy of what an assessment says about a child that
   * leaves Haven, and saving one is a deliberate step, not a keystroke.
   */
  assessment_pdf_downloaded: "assessment_pdf_downloaded",
  /**
   * A guest opened an assessment shared with them. Recorded, unlike other
   * reads, because somebody outside every organisation reaching what an
   * assessment says about a person is an act those answerable for the person
   * are owed sight of, and opening one from the short list shared is a
   * deliberate step, not a keystroke.
   */
  shared_assessment_read: "shared_assessment_read",
  /**
   * An approver approved a definitive assessment's latest submission, which
   * makes it the one that stands.
   */
  assessment_approved: "assessment_approved",
  /**
   * An approver returned a definitive assessment's latest submission to its
   * owner. Why stays on the decision, where reach governs who reads it.
   */
  assessment_returned: "assessment_returned",
  /**
   * A returned assessment was taken up again by its owner, who chose to, with a
   * new draft started from one of its submissions.
   */
  assessment_reopened: "assessment_reopened",
  /**
   * A level of the configuration chain was created, renamed, or moved to sit
   * under a different one.
   *
   * Moving a scope changes what every body beneath it resolves to, which is why
   * it is recorded rather than left to the scope's own history table: the
   * history says what the row became, and this says who decided it and in what
   * session context.
   */
  configuration_scope_created: "configuration_scope_created",
  configuration_scope_updated: "configuration_scope_updated",
  /** An organisation was attached to a level of the chain, or detached from one. */
  configuration_organisation_scope_changed:
    "configuration_organisation_scope_changed",
  /**
   * A setting was given a value at one level of the chain, or stopped being
   * stated there so that it inherits again.
   *
   * Two kinds rather than one with a nullable value, because they are different
   * acts: setting a value fixes it whatever happens above, and clearing one hands
   * the decision back to the layer above — which is not visible from the value
   * afterwards, since both leave the body resolving to something.
   */
  configuration_setting_set: "configuration_setting_set",
  configuration_setting_cleared: "configuration_setting_cleared",
  /**
   * Somebody was named to an involvement with a young person — a duty of care,
   * a placement, an assessor. This is how access is granted, so who did it and
   * when is the whole point.
   *
   * The party is named as a resource and the payload carries the kind of
   * involvement. Neither carries the young person's name: the trail is exported
   * and read more widely than the record is.
   */
  involvement_started: "involvement_started",
  /**
   * An involvement was ended, and whoever held it stopped reaching the child.
   * Recorded with the same weight as granting one: withdrawal is a decision
   * somebody made, which is precisely why nothing here ends an involvement on
   * its own.
   */
  involvement_ended: "involvement_ended",
  /**
   * An organisation was given its kind — a local authority, a provider, a
   * school. The kind decides which part a body may take in a child's care, so
   * setting it decides who can be handed a duty of care, and is recorded as a
   * grant of access would be.
   */
  organisation_kind_set: "organisation_kind_set",
  /**
   * An organisation was created, with its name and kind. Recorded because a new
   * body is somewhere memberships can be granted and a duty of care held, so
   * who brought it into being is asked as often as who was given access through
   * it.
   */
  organisation_created: "organisation_created",
  /**
   * An organisation was set to read, or no longer to read, what its members'
   * finished assessments say. It decides who reads what is concluded about a
   * child, so it is recorded as a grant of access would be.
   */
  organisation_reads_approved_assessments_through_members_set:
    "organisation_reads_approved_assessments_through_members_set",
  /** An organisation was renamed. */
  organisation_renamed: "organisation_renamed",
  /**
   * A guest was given a link to the involvement they hold. The link is the
   * entry's resource; the address it was sent to is held on the link, not
   * here.
   */
  guest_link_created: "guest_link_created",
  /**
   * Somebody shared assessments with a guest, through the guest's involvement.
   * The person, the involvement and each assessment shared are the entry's
   * resources.
   */
  guest_assessments_shared: "guest_assessments_shared",
  /** A code to sign in with was emailed to a guest access link's address. */
  guest_code_sent: "guest_code_sent",
  /**
   * A guest gave a wrong code or a wrong date of birth. Each counts towards
   * the link's lock, so each is recorded.
   */
  guest_session_sign_in_failure: "guest_session_sign_in_failure",
  /** A guest gave the right code and date of birth through their link. */
  guest_session_sign_in_success: "guest_session_sign_in_success",
  /** A wrong try used the last a guest access link takes, and locked it. */
  guest_link_locked: "guest_link_locked",
  /** A guest accepted the information governance wording they were shown. */
  guest_agreement_accepted: "guest_agreement_accepted",
  /**
   * A guest said their link was not meant for them, which ended their access
   * with them as its author.
   */
  guest_link_declined: "guest_link_declined",
  /**
   * One organisation was recorded as standing to another: a member of it, or
   * operated by it. Membership carries what a member holds for a child up to
   * the organisation above, so it is recorded as a grant of access would be.
   */
  organisation_relationship_added: "organisation_relationship_added",
  /** A relationship between two organisations was removed. */
  organisation_relationship_removed: "organisation_relationship_removed",
} as const;

export type AuditTrailEntryKind =
  (typeof AuditTrailEntryKind)[keyof typeof AuditTrailEntryKind];

export const AuditTrailEntryKindSchema = z.enum(
  AuditTrailEntryKind,
) satisfies ZodType<AuditTrailEntryKind & SnakeCase<AuditTrailEntryKind>>;
